import { get, type Attendance, type BookingLesson, type Me } from "../api.js";
import { formatDate, formatTime } from "../format.js";
import type { LessonCreditBlock } from "./api.js";
import { diaryManualCreditUseAllowed, diaryPaymentCanResolveOnSite, diaryPaymentClassifications, type DiaryPaymentClassification } from "./diaryPayments.js";
import { ameliaAttendance, getLessonOutcome, type LessonOutcome } from "./lessonOutcome.js";

export const MAX_SELECTED_LESSONS = 50;

export interface SelectedLesson {
  bookingId: string;
  appointmentId: number;
  startsAt: string;
  endsAt: string | null;
  serviceName: string | null;
}

export function selectedLessonLabel(lesson: SelectedLesson): string {
  return `${formatDate(lesson.startsAt)} at ${formatTime(lesson.startsAt)} · ${lesson.serviceName ?? "Lesson"}`;
}

export function lessonSelectionAccess(me: Me | null | undefined) {
  const staff = me?.booking.staff === true && (me.roles.includes("owner") || me.roles.includes("tutor"));
  return { cancel: staff, credit: staff && me.capabilities.includes("payments.manage") };
}

export interface CheckedLesson {
  lesson: SelectedLesson;
  outcome: LessonOutcome | null;
  payment: DiaryPaymentClassification | null;
  recordedAttendance: Attendance | null;
  problem: string | null;
}

/** Re-read the pupil's booking as well as the live appointment before offering a change. */
export async function checkSelectedLessons(studentId: string, lessons: SelectedLesson[], payments = false): Promise<CheckedLesson[]> {
  if (!lessons.length || lessons.length > MAX_SELECTED_LESSONS || new Set(lessons.map(l => l.appointmentId)).size !== lessons.length) {
    throw new Error(`Select between 1 and ${MAX_SELECTED_LESSONS} different lessons.`);
  }
  const classifications = payments ? await diaryPaymentClassifications(lessons.map(l => l.appointmentId)) : [];
  const result: CheckedLesson[] = [];
  // Bound concurrent reads even when several pages have been selected.
  for (let offset = 0; offset < lessons.length; offset += 5) {
    result.push(...await Promise.all(lessons.slice(offset, offset + 5).map(async lesson => {
      try {
        const { booking, lesson: note } = await get<BookingLesson>(`/admin/bookings/${encodeURIComponent(lesson.bookingId)}/lesson`);
        if (booking.bookingId !== lesson.bookingId || booking.studentId !== studentId || booking.ameliaAppointmentId !== lesson.appointmentId || !booking.ameliaBookingId || Date.parse(booking.startsAt) !== Date.parse(lesson.startsAt)) {
          throw new Error("This lesson has changed. Refresh the lesson list before selecting it again.");
        }
        const outcome = await getLessonOutcome(lesson.appointmentId);
        const attendance = ameliaAttendance(outcome, booking.ameliaBookingId);
        if (outcome.appointment_id !== lesson.appointmentId || attendance === "unknown") throw new Error("This student's place in the lesson could not be confirmed.");
        if (outcome.mutation_scope !== "single_participant" || outcome.participant_count !== 1) throw new Error("Shared lessons must be managed individually in the Diary.");
        if (attendance !== "active" && attendance !== "no-show") throw new Error("This lesson is no longer active.");
        const matched = classifications.filter(p => p.appointment_id === lesson.appointmentId);
        const payment = matched.length === 1 ? matched[0]! : null;
        if (payments && (!payment || (payment.booking_id != null && payment.booking_id !== booking.ameliaBookingId))) throw new Error("The payment for this student's lesson could not be confirmed.");
        return { lesson, outcome, payment, recordedAttendance: note?.attendanceStatus ?? null, problem: null };
      } catch (cause) {
        return { lesson, outcome: null, payment: null, recordedAttendance: null, problem: cause instanceof Error ? cause.message : "This lesson could not be checked." };
      }
    })));
  }
  return result;
}

export function selectedCreditProblem(row: CheckedLesson, block: LessonCreditBlock): string | null {
  if (row.problem) return row.problem;
  if (!row.outcome || !diaryPaymentCanResolveOnSite(row.payment)) return "Only unpaid on-site lessons can use a block credit.";
  if (!diaryManualCreditUseAllowed(block)) return block.manual_use?.reasons.map(r => r.message).join(" ") || "This block cannot be applied manually.";
  if (block.service_id !== row.outcome.service_id || block.provider_id !== row.outcome.provider_id) return "This block is for a different lesson type or tutor.";
  // The API supplies an exact expiry instant, not a date valid through midnight.
  const startsAt = Date.parse(row.lesson.startsAt);
  const expiresAt = Date.parse(block.expires_at);
  if (!Number.isFinite(startsAt) || !Number.isFinite(expiresAt)) return "The lesson date or block expiry could not be confirmed.";
  if (startsAt >= expiresAt) return "This lesson is at or after the block's expiry time.";
  return null;
}

export function selectedCancellationProblem(row: CheckedLesson): string | null {
  if (row.problem) return row.problem;
  if (row.recordedAttendance === "present" || row.recordedAttendance === "late") {
    return "This lesson is recorded as attended or late. Review its attendance individually before cancelling it.";
  }
  return row.outcome?.can_cancel ? null : "This lesson cannot be cancelled here.";
}
